import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { catchError, EMPTY, filter, from, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';

@Injectable()
export class EntraTokenInterceptor implements HttpInterceptor {
    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService
    ) {}

    shouldRefreshIdToken(account: AccountInfo): boolean {
        const exp = account.idTokenClaims?.exp;

        if (typeof exp !== 'number') {
            return true;
        }

        const now = Math.floor(Date.now() / 1000);

        return exp <= now + 300;
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!request.url.startsWith(`${environment.api_url}`)) {
            return next.handle(request);
        }

        return this.msalBroadcastService.inProgress$.pipe(
            filter((status) => status === InteractionStatus.None),
            take(1),
            switchMap(() => {
                const account = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];

                if (!account) {
                    return next.handle(request);
                }

                const forceRefresh = this.shouldRefreshIdToken(account);

                return from(
                    this.msalService.instance.acquireTokenSilent({
                        account,
                        scopes: environment.msal_scope,
                        forceRefresh,
                    })
                ).pipe(
                    switchMap((result: AuthenticationResult) => {
                        const authReq = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${result.idToken}`,
                            },
                        });

                        return next.handle(authReq);
                    })
                );
            }),
            catchError((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    this.msalService.loginRedirect({
                        scopes: environment.msal_scope,
                    });

                    return EMPTY;
                }

                return throwError(() => error);
            })
        );
    }
}
